import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {useSafeChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {AUTO_MODEL_ID} from '@github-ui/copilot-chat-helpers/auto-model'
import {
  ALMOST_OUT_THRESHOLD,
  APPROACHING_THRESHOLD,
  ENABLE_ADDITIONAL_REQUESTS_URL,
  FREE_QUOTA_TRIGGER,
  MANAGE_BILLING_URL,
  MANAGE_BUDGET_URL,
  PREMIUM_QUOTA_TRIGGER,
  UPGRADE_PLAN_URL,
  UPGRADE_PLAN_URL_NEW,
} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {type CopilotChatMode, type CopilotChatModel, CopilotPlan} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {formatQuotaResetLabel} from '../../utils/format-quota-reset-label'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from '../../utils/portal-roots'
import {sendEvent} from '@github-ui/hydro-analytics'
import {SyncIcon, TriangleDownIcon} from '@primer/octicons-react'
import {
  ActionList,
  ActionMenu,
  ButtonGroup,
  IconButton,
  Link,
  PortalContext,
  SkeletonBox,
  Tooltip as PrimerTooltip,
} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import React, {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {ModelListItem, ModelVendorIcon} from './ModelListItem'
import styles from './ModelPicker.module.css'
import {
  type AutoModelTier as AutoModelTierDefinition,
  type AutoModelTierState as AutoModelTierStateDefinition,
  type AutoTierId as AutoTierIdDefinition,
  type AutoTierOption as AutoTierOptionDefinition,
  type AutoTierPickerConfig as AutoTierPickerConfigDefinition,
  AutoTierAnnouncements,
  getAutoTierLabel as getAutoTierLabelDefinition,
  getConfiguredAutoTierLabel as getConfiguredAutoTierLabelDefinition,
  getAutoTierModelAccessibleLabel,
  getAutoTierModelDisplayName,
  isAutoModelTier as isAutoModelTierDefinition,
  isVisibleAutoTierPickerConfig,
  type VisibleAutoTierPickerConfig as VisibleAutoTierPickerConfigDefinition,
} from './AutoTierPicker'
import {
  categorizeModels,
  type CustomModelGroup,
  getOnlyEnabledModel,
  hasModelCategoryDiversity,
  isSameModel,
} from './model-picker-helpers'

const MODEL_PICKER_FEATURE_FLAGS = {primer_react_css_anchor_positioning: false}
const useIsoLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect

export const ModelPickerVisibilityContext = createContext(true)
export const ModelPickerPortalContext = createContext<React.ContextType<typeof PortalContext> | undefined>(undefined)

/**
 * Which composing surface the picker is attached to: the global chip beside the
 * composer, or the retry control on a message.
 */
export type ModelPickerType = 'global' | 'message-retry'

export type AutoModelTier = AutoModelTierDefinition
export type AutoModelTierState = AutoModelTierStateDefinition
export type AutoTierId = AutoTierIdDefinition
export type AutoTierOption = AutoTierOptionDefinition
export type AutoTierPickerConfig = AutoTierPickerConfigDefinition
export type VisibleAutoTierPickerConfig = VisibleAutoTierPickerConfigDefinition
export const getAutoTierLabel = getAutoTierLabelDefinition
export const getConfiguredAutoTierLabel = getConfiguredAutoTierLabelDefinition
export const isAutoModelTier = isAutoModelTierDefinition

type SharedModelPickerDesignProps = {
  disabled: boolean
  'aria-describedby'?: string
  variant?: 'default' | 'invisible'
  /** Chat mode for portal rendering - defaults to 'immersive' */
  mode?: CopilotChatMode
  /**
   * When false, the global chip trigger hides the leading vendor/Copilot icon and
   * shows the model name only. Defaults to true to preserve existing trigger styling.
   */
  showTriggerIcon?: boolean
  /**
   * Optional size for the global chip trigger button. Defaults to undefined so the
   * trigger keeps Primer's default (medium) size for existing consumers. The env-strip
   * toolbar passes 'medium' because Primer's ActionMenu.Button clips text at 'small'.
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Opts the trigger into the composer's container-query compaction. It only
   * marks the trigger and its label; the container query decides when to
   * collapse them. The model name moves onto `aria-label` at the same time, so
   * hiding the visible label cannot take the accessible name with it.
   */
  responsiveIconOnly?: boolean
  /** Positions compact-trigger tooltips away from a constrained composer edge. */
  tooltipDirection?: React.ComponentProps<typeof PrimerTooltip>['direction']
  /** Called when the menu opens or closes. Hosts use it for telemetry; it cannot veto the change. */
  onOpenChange?: (open: boolean) => void
}

type ReadyModelPickerDesignProps = SharedModelPickerDesignProps & {
  isLoading?: false
  selectedModel: CopilotChatModel
  models: CopilotChatModel[]
  onModelPicked: (m: CopilotChatModel) => void
  type: ModelPickerType
  showCategories?: boolean
  isTokenBasedBillingEnabled?: boolean
  /** Org-backed catalogs are authoritative; the editor's personal plan and quota do not apply. */
  useCatalogEligibility?: boolean
  showPreviewModelsSeparately?: boolean
  /**
   * Model id the Auto router pinned for the active thread (from `thread.autoPickedModel`).
   * When set (and the chip is showing Auto), the chip and Auto row reveal this model's
   * display name on hover/focus, and the Auto entry's info popover surfaces its details.
   */
  autoPickedModel?: string
  /**
   * Discount ratios for the thread's Auto Mode suite. Stored here so the
   * downstream info popover can render discounted cost without another fetch.
   */
  autoModeDiscounts?: Record<string, number>
  /** Model identified by EMS as the organization's default for new sessions. */
  organizationDefaultModelId?: string
  /** Controlled Auto-tier presentation. Omit it when the host cannot or should not expose tier selection. */
  autoTierPicker?: AutoTierPickerConfig
}

type LoadingModelPickerDesignProps = SharedModelPickerDesignProps & {
  isLoading: true
  type: 'global'
  selectedModel?: never
  models?: never
  onModelPicked?: never
  showCategories?: never
  isTokenBasedBillingEnabled?: never
  useCatalogEligibility?: never
  showPreviewModelsSeparately?: never
  autoPickedModel?: never
  autoModeDiscounts?: never
  organizationDefaultModelId?: never
  autoTierPicker?: never
}

type ModelPickerDesignProps = ReadyModelPickerDesignProps | LoadingModelPickerDesignProps

function getAutoPinnedModelDiscount(autoModeDiscounts: Record<string, number> | undefined, autoPinnedModelId?: string) {
  if (!autoModeDiscounts || !autoPinnedModelId) return undefined
  if (!Object.prototype.hasOwnProperty.call(autoModeDiscounts, autoPinnedModelId)) return 0

  const discount = autoModeDiscounts[autoPinnedModelId]
  return typeof discount === 'number' && Number.isFinite(discount) && discount >= 0 && discount <= 1
    ? discount
    : undefined
}

interface RetryButtonGroupProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
  disabled: boolean
  ref?: React.Ref<HTMLDivElement>
}

function RetryButtonGroup({ref, ...props}: RetryButtonGroupProps) {
  const {selectedModel, onModelPicked, disabled, ...buttonProps} = props
  return (
    <ButtonGroup className={styles.messageRetryButtonGroup} ref={ref}>
      <IconButton
        variant="invisible"
        aria-label={`Retry with ${selectedModel.displayName}`}
        onClick={() => onModelPicked(selectedModel)}
        icon={SyncIcon}
        disabled={disabled}
        className={styles.messageRetryButton}
      />
      <div className={styles.messageRetryDivider} />
      <IconButton
        variant="invisible"
        icon={TriangleDownIcon}
        aria-label="Retry with…"
        disabled={disabled}
        className={styles.messageRetryMenuButton}
        {...buttonProps}
      />
    </ButtonGroup>
  )
}

export function ModelPickerDesign(props: ModelPickerDesignProps) {
  const modelPickerPortal = use(ModelPickerPortalContext)
  const inheritedPortal = use(PortalContext)
  // Native anchor positioning can reset a scrolled menu before Android dispatches the compatibility click.
  return (
    <FeatureFlags flags={MODEL_PICKER_FEATURE_FLAGS}>
      <PortalContext value={modelPickerPortal ?? inheritedPortal}>
        <ModelPickerDesignContent {...props} />
      </PortalContext>
    </FeatureFlags>
  )
}

function ModelPickerDesignContent({
  disabled,
  variant = 'invisible',
  mode = 'immersive',
  showTriggerIcon = true,
  size,
  responsiveIconOnly,
  tooltipDirection,
  onOpenChange,
  ...props
}: ModelPickerDesignProps) {
  const {type, isLoading = false} = props
  const isVisible = use(ModelPickerVisibilityContext)
  const {portalContainerName: inheritedPortalContainerName} = use(PortalContext)
  const portalContainerName = mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : inheritedPortalContainerName
  const [isOpen, setIsOpen] = useState(false)
  // Whether the container query actually collapsed the trigger, measured from
  // the rendered width so the tooltip names the model exactly when its visible
  // label is gone.
  const [compact, setCompact] = useState(false)
  const updateCompact = (target: HTMLElement) => {
    setCompact(current => {
      const next = target.getBoundingClientRect().width <= 32
      return current === next ? current : next
    })
  }
  const preferredOverlaySide = useOverlayDirection()
  const readyProps = props.isLoading === true ? undefined : props
  const isReady = readyProps !== undefined
  const selectedModelId = readyProps?.selectedModel.id
  const visibleAutoTierPicker =
    readyProps?.type === 'global' &&
    readyProps.selectedModel.id === AUTO_MODEL_ID &&
    isVisibleAutoTierPickerConfig(readyProps.autoTierPicker)
      ? readyProps.autoTierPicker
      : undefined
  const selectedModelDisplayName =
    readyProps && visibleAutoTierPicker
      ? getAutoTierModelDisplayName(readyProps.selectedModel.displayName, visibleAutoTierPicker)
      : readyProps?.selectedModel.displayName
  const selectedModelAccessibleLabel =
    readyProps && visibleAutoTierPicker
      ? getAutoTierModelAccessibleLabel(readyProps.selectedModel.displayName, visibleAutoTierPicker)
      : undefined

  const changeMenuOpen = useCallback(
    (next: boolean) => {
      setIsOpen(next)
      if (next !== isOpen) onOpenChange?.(next)
    },
    [isOpen, onOpenChange],
  )

  const handleMenuOpen = (open: boolean) => {
    // In case the retry divider is clicked
    const shouldOpen = isVisible && !disabled && isReady && open
    changeMenuOpen(shouldOpen)

    if (shouldOpen && selectedModelId) {
      sendEvent('dotcom_chat.activate', {
        target: 'MODEL_PICKER_OPEN',
        mode,
        model: selectedModelId,
        type,
      })
    }
  }

  useIsoLayoutEffect(() => {
    if (!isVisible && isOpen) changeMenuOpen(false)
  }, [changeMenuOpen, isOpen, isVisible])

  const componentRef = useRef<HTMLDivElement>(null)
  const [menuDirection, setMenuDirection] = useState<'up' | 'down'>('down')

  // When the thread has a pinned Auto model, reveal that model's display name on
  // hover/focus — both on the Auto chip and on the Auto row inside the menu. Resolve
  // the display name from the same model list the picker renders so names stay
  // consistent; fall back to the raw id when the pinned model is deprecated or no
  // longer in the user's list.
  const autoPinnedModelId = readyProps?.autoPickedModel?.trim()
  const pinnedModelDisplayName =
    readyProps && type === 'global' && autoPinnedModelId
      ? (readyProps.models.find(m => m.id === autoPinnedModelId)?.displayName ?? autoPinnedModelId)
      : undefined
  // The chip only reveals the pinned model when Auto is the active selection.
  const chipPinnedModelDisplayName = readyProps?.selectedModel.id === AUTO_MODEL_ID ? pinnedModelDisplayName : undefined
  const autoModelDisplayName =
    visibleAutoTierPicker?.autoModelDisplayName ?? readyProps?.selectedModel.displayName ?? 'Auto'
  const chipPinnedModelDescription = chipPinnedModelDisplayName
    ? `${autoModelDisplayName} is currently using ${chipPinnedModelDisplayName}`
    : undefined

  const globalChipButton = (
    <ActionMenu.Button
      className={styles.menuButton}
      variant={variant}
      size={size}
      disabled={disabled}
      inactive={isLoading || undefined}
      aria-disabled={isLoading || undefined}
      aria-describedby={props['aria-describedby']}
      aria-label={
        isLoading
          ? 'Loading models'
          : selectedModelAccessibleLabel
            ? selectedModelAccessibleLabel
            : responsiveIconOnly && readyProps
              ? `Model: ${readyProps.selectedModel.displayName}`
              : undefined
      }
      data-responsive-compact-control={responsiveIconOnly ? 'model' : undefined}
      onPointerOverCapture={event => updateCompact(event.currentTarget)}
      onFocusCapture={event => updateCompact(event.currentTarget)}
      // Show the selected model's associated icon in the trigger — the AI model icon
      // for Auto, the vendor/provider logo otherwise — using the same ModelVendorIcon
      // the menu rows render. The icon is decorative next to the visible model name
      // (the button already labels itself via the sr-only "Model:" + display name), so
      // pass `decorative` to suppress the vendor-logo alt text and avoid a redundant
      // screen-reader announcement.
      leadingVisual={
        showTriggerIcon && readyProps
          ? () => (
              <span className={styles.triggerIcon}>
                <ModelVendorIcon model={readyProps.selectedModel} decorative />
              </span>
            )
          : undefined
      }
    >
      {readyProps ? (
        <>
          {/* Withheld while compact: `aria-label` already names the model, so
              leaving this would make the name announce twice. */}
          {responsiveIconOnly ? null : <span className="sr-only">Model: </span>}
          {responsiveIconOnly && !showTriggerIcon ? (
            <span className={styles.compactIcon} data-responsive-compact-icon="" aria-hidden="true">
              <ModelVendorIcon model={readyProps.selectedModel} decorative />
            </span>
          ) : null}
          <div className={styles.buttonName} data-responsive-compact-label={responsiveIconOnly ? '' : undefined}>
            {selectedModelDisplayName}
          </div>
        </>
      ) : (
        <SkeletonBox width={64} height={16} aria-hidden="true" data-testid="model-picker-skeleton" />
      )}
    </ActionMenu.Button>
  )

  return (
    <div ref={componentRef}>
      {visibleAutoTierPicker ? <AutoTierAnnouncements config={visibleAutoTierPicker} /> : null}
      <ActionMenu open={isVisible && isReady && isOpen} onOpenChange={handleMenuOpen}>
        {type === 'global' || !readyProps ? (
          // Skip the tooltip while the chip is disabled (e.g. during streaming): Primer's
          // TooltipV2 throws an invariant when its trigger is a native `disabled` button,
          // which crashes the chat via the error boundary. The pinned-model hint isn't
          // reachable on a disabled control anyway, so render the bare button instead.
          chipPinnedModelDisplayName && !disabled ? (
            // ActionMenu only threads its anchor props through a Primer Tooltip (TooltipV2)
            // wrapping ActionMenu.Button — it matches on that component's identity. A non-Primer
            // wrapper (e.g. pacer Tooltip) is not recognized as the anchor and breaks menu
            // positioning/ARIA, so this must stay Primer's Tooltip and remain a direct child.
            <PrimerTooltip
              text={`${autoModelDisplayName} (${chipPinnedModelDisplayName})`}
              type="description"
              aria-label={chipPinnedModelDescription}
              className={styles.pinnedModelTooltip}
              direction={tooltipDirection}
            >
              {globalChipButton}
            </PrimerTooltip>
          ) : responsiveIconOnly && !disabled && readyProps ? (
            // Once the container query hides the label this trigger is a bare
            // icon, so it says which model it selects. Primer's Tooltip must
            // remain the direct wrapper for ActionMenu's anchor matching.
            <PrimerTooltip text={compact ? `Model: ${selectedModelDisplayName}` : 'Model'} direction={tooltipDirection}>
              {globalChipButton}
            </PrimerTooltip>
          ) : (
            globalChipButton
          )
        ) : (
          <ActionMenu.Anchor aria-label={'Retry with model'}>
            <RetryButtonGroup
              selectedModel={readyProps.selectedModel}
              onModelPicked={readyProps.onModelPicked}
              disabled={disabled}
            />
          </ActionMenu.Anchor>
        )}

        <ActionMenu.Overlay
          width={type === 'global' ? 'medium' : 'small'}
          side={preferredOverlaySide}
          className={styles.actionMenuOverlay}
          maxHeight="fit-content"
          align={visibleAutoTierPicker ? 'start' : 'end'}
          position={mode === 'assistive' ? 'relative' : undefined}
          displayInViewport={mode === 'immersive' ? !portalContainerName : undefined}
          portalContainerName={portalContainerName}
          style={
            mode === 'immersive' && portalContainerName
              ? {
                  maxHeight:
                    'max(0px, calc(var(--max-overlay-height, min(100dvh - 32px, 600px)) - var(--base-size-16)))',
                }
              : undefined
          }
          onPositionChange={
            type === 'message-retry'
              ? ({position}) => {
                  const downPositions = ['inside-bottom', 'outside-bottom']
                  setMenuDirection(downPositions.includes(position.anchorSide) ? 'down' : 'up')
                }
              : undefined
          }
        >
          {readyProps ? (
            <ReadyModelPickerMenu
              {...readyProps}
              menuDirection={menuDirection}
              autoTierPicker={visibleAutoTierPicker}
              autoTierPortalContainerName={portalContainerName}
            />
          ) : null}
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

type ReadyModelPickerMenuProps = Pick<
  ReadyModelPickerDesignProps,
  | 'selectedModel'
  | 'models'
  | 'onModelPicked'
  | 'type'
  | 'showCategories'
  | 'isTokenBasedBillingEnabled'
  | 'useCatalogEligibility'
  | 'showPreviewModelsSeparately'
  | 'autoPickedModel'
  | 'autoModeDiscounts'
  | 'organizationDefaultModelId'
> & {
  menuDirection: 'up' | 'down'
  autoTierPicker?: VisibleAutoTierPickerConfig
  autoTierPortalContainerName?: string
}

function ReadyModelPickerMenu({
  selectedModel,
  models,
  onModelPicked,
  type,
  showCategories,
  isTokenBasedBillingEnabled = false,
  useCatalogEligibility = false,
  showPreviewModelsSeparately = true,
  autoPickedModel,
  autoModeDiscounts,
  organizationDefaultModelId,
  menuDirection,
  autoTierPicker,
  autoTierPortalContainerName,
}: ReadyModelPickerMenuProps) {
  const {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled} = useEntitlement()
  // If the resolved default is the Auto pseudo-entry (e.g. plan-aware fallback
  // from generateDefaultModelForPlan when CAPI omits is_chat_default) but the
  // backend list doesn't contain Auto, inject the selected model itself so
  // the menu matches the button.
  const augmentedModels =
    selectedModel.id === AUTO_MODEL_ID &&
    selectedModel.model_picker_enabled &&
    !models.some(m => m.id === AUTO_MODEL_ID)
      ? [selectedModel, ...models]
      : models

  const shouldShowModelCategories = showCategories !== false && hasModelCategoryDiversity(augmentedModels)
  const modelTiers = categorizeModels(augmentedModels, {
    shouldShowCategories: shouldShowModelCategories,
    shouldShowPreviewModelsSeparately: showPreviewModelsSeparately,
    modelLockContext: useCatalogEligibility
      ? 'catalog'
      : {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled},
  })
  const {
    lowTier: lowTierModels,
    midTier: midTierModels,
    highTier: highTierModels,
    base: baseModels,
    preview: previewModels,
    custom: customModelGroups,
  } = modelTiers
  const isSingleEnabledModelPicker = type === 'global' && getOnlyEnabledModel(augmentedModels) !== null
  const hasCustomModels = Object.keys(customModelGroups).length > 0
  const autoPinnedModelId = autoPickedModel?.trim()
  const autoPinnedModel = autoPinnedModelId ? augmentedModels.find(m => m.id === autoPinnedModelId) : undefined
  const autoPinnedModelDiscount = getAutoPinnedModelDiscount(autoModeDiscounts, autoPinnedModelId)
  const pinnedModelDisplayName =
    type === 'global' && autoPinnedModelId
      ? (models.find(m => m.id === autoPinnedModelId)?.displayName ?? autoPinnedModelId)
      : undefined

  const renderModelItem = (m: CopilotChatModel) => {
    const selected = isSameModel(selectedModel, m) && type === 'global'

    return (
      <ModelListItem
        key={m.id}
        model={m}
        selected={selected}
        onModelPicked={onModelPicked}
        isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
        useCatalogEligibility={useCatalogEligibility}
        autoPinnedModelDisplayName={pinnedModelDisplayName}
        autoPinnedModel={m.id === AUTO_MODEL_ID ? autoPinnedModel : undefined}
        autoPinnedModelDiscount={m.id === AUTO_MODEL_ID ? autoPinnedModelDiscount : undefined}
        autoModeDiscounts={m.id === AUTO_MODEL_ID ? autoModeDiscounts : undefined}
        organizationDefault={m.id === organizationDefaultModelId}
        autoTierPicker={m.id === AUTO_MODEL_ID ? autoTierPicker : undefined}
        autoTierPortalContainerName={m.id === AUTO_MODEL_ID ? autoTierPortalContainerName : undefined}
      />
    )
  }

  return (
    <ActionList className={styles.actionList} selectionVariant={type === 'global' ? 'single' : undefined}>
      {type === 'message-retry' && menuDirection === 'down' && (
        <>
          <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
          <ActionList.Divider />
        </>
      )}
      {type === 'global' && <div className={styles.menuHeading}>Models</div>}
      {!useCatalogEligibility && (
        <ModelPickerHeader
          isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
          hasSingleEnabledModel={isSingleEnabledModelPicker}
        />
      )}

      {baseModels.length > 0 && (
        <ActionList.Group>
          {type !== 'global' && <ActionList.GroupHeading>Models</ActionList.GroupHeading>}
          {baseModels.map(renderModelItem)}
          {(lowTierModels.length > 0 ||
            midTierModels.length > 0 ||
            highTierModels.length > 0 ||
            previewModels.length > 0 ||
            hasCustomModels) && <ActionList.Divider />}
        </ActionList.Group>
      )}

      {lowTierModels.length > 0 && (
        <ActionList.Group>
          <ActionList.GroupHeading>Fast and cost-efficient</ActionList.GroupHeading>
          {lowTierModels.map(renderModelItem)}
          {(midTierModels.length > 0 || highTierModels.length > 0) && <ActionList.Divider />}
        </ActionList.Group>
      )}

      {midTierModels.length > 0 && (
        <ActionList.Group>
          <ActionList.GroupHeading>Versatile and highly intelligent</ActionList.GroupHeading>
          {midTierModels.map(renderModelItem)}
          {(highTierModels.length > 0 || previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
        </ActionList.Group>
      )}

      {highTierModels.length > 0 && (
        <ActionList.Group>
          <ActionList.GroupHeading>Most powerful at complex tasks</ActionList.GroupHeading>
          {highTierModels.map(renderModelItem)}
          {(previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
        </ActionList.Group>
      )}

      {previewModels.length > 0 && (
        <ActionList.Group>
          <ActionList.GroupHeading>Preview</ActionList.GroupHeading>
          {previewModels.map(renderModelItem)}
          {hasCustomModels && <ActionList.Divider />}
        </ActionList.Group>
      )}

      {hasCustomModels && (
        <CustomModelActionListGroups customModelGroups={customModelGroups}>
          {renderModelItem}
        </CustomModelActionListGroups>
      )}

      {type === 'message-retry' && menuDirection === 'up' && (
        <>
          <ActionList.Divider />
          <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
        </>
      )}
    </ActionList>
  )
}

function CustomModelActionListGroups({
  customModelGroups,
  children,
}: {
  customModelGroups: {[groupKey: string]: CustomModelGroup}
  children: (model: CopilotChatModel) => ReactNode
}) {
  const groupKeys = Object.keys(customModelGroups)
  const lastGroupIndex = groupKeys.length - 1

  return groupKeys.map((groupKey, i) => {
    const group = customModelGroups[groupKey]
    if (!group) return null
    return (
      <ActionList.Group key={groupKey}>
        <ActionList.GroupHeading>{group.displayName}</ActionList.GroupHeading>
        {group.models.map(children)}
        {i < lastGroupIndex && <ActionList.Divider />}
      </ActionList.Group>
    )
  })
}

interface RetryListItemProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
}

function RetryListItem({selectedModel, onModelPicked}: RetryListItemProps) {
  return (
    <ActionList.Item key={selectedModel.id} onSelect={() => onModelPicked(selectedModel)}>
      <ActionList.LeadingVisual>
        <SyncIcon />
      </ActionList.LeadingVisual>
      Try again
      <ActionList.Description variant="block" truncate>
        {selectedModel.displayName}
      </ActionList.Description>
    </ActionList.Item>
  )
}

type EntitlementSnapshot = ReturnType<typeof useEntitlement>

function ModelPickerHeader({
  isTokenBasedBillingEnabled = false,
  hasSingleEnabledModel = false,
}: {
  isTokenBasedBillingEnabled?: boolean
  hasSingleEnabledModel?: boolean
}) {
  const entitlement = useEntitlement()

  const content =
    isTokenBasedBillingEnabled || hasSingleEnabledModel
      ? renderUbbContent(entitlement, hasSingleEnabledModel)
      : renderLegacyContent(entitlement)

  return content ? <div className={styles.header}>{content}</div> : null
}

// UBB users see a per-SKU quota message when one applies. When no scenario
// applies, render nothing. The backend-curated picker doesn't surface a
// generic pricing-details link because the user can't freely choose among
// the models.
function renderUbbContent(entitlement: EntitlementSnapshot, hasSingleEnabledModel = false): ReactNode {
  const scenarioContent = renderUbbScenario(
    detectQuotaScenario(entitlement, UBB_THRESHOLDS),
    entitlement,
    hasSingleEnabledModel,
  )
  if (scenarioContent) return scenarioContent
  if (hasSingleEnabledModel && entitlement.canUpgradePlan) {
    return (
      <>
        <UpgradeLink url={UPGRADE_PLAN_URL_NEW} context="single-enabled-model" /> to access more models and higher
        limits.
      </>
    )
  }
  return null
}

function renderLegacyContent(entitlement: EntitlementSnapshot): ReactNode {
  return renderLegacyScenario(detectQuotaScenario(entitlement, LEGACY_THRESHOLDS), entitlement)
}

// Stable discriminator describing which footer scenario an Upgrade click came
// from, so conversion can be measured per surface without a brittle copy match.
// Derived from QuotaScenario (plus the single-enabled-model header link) rather than
// hand-coded strings.
type UpgradeLinkContext = QuotaScenario | 'single-enabled-model' | `${QuotaScenario}-single-enabled-model`

// Shared "Upgrade" link used by both the UBB and legacy footer paths. Fires a
// hydro click event before navigation (mirroring QuotaBanner) so the event lands
// even though the link unloads the page.
const UpgradeLink = ({context, url = UPGRADE_PLAN_URL}: {context: UpgradeLinkContext; url?: string}) => {
  const mode = useSafeChatState()?.mode ?? 'unknown'
  const plan = useEntitlement().plan ?? 'unknown'
  return (
    <Link
      inline
      href={url}
      onClick={() => {
        sendEvent('dotcom_chat.activate', {
          target: 'MODEL_PICKER_UPGRADE',
          mode,
          plan,
          context,
        })
      }}
    >
      Upgrade
    </Link>
  )
}

const ManageBillingLink = ({url = MANAGE_BILLING_URL}: {url?: string}) => (
  <Link inline href={url}>
    Manage billing
  </Link>
)

const EnableAdditionalLink = ({label, url = ENABLE_ADDITIONAL_REQUESTS_URL}: {label: string; url?: string}) => (
  <Link inline href={url}>
    {label}
  </Link>
)

/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
const AdminBold = ({label}: {label: string}) => <span className="font-weight-bold">{label} </span>

function formatUsagePercent(percentRemaining: number): number {
  // Mirror QuotaBanner: display the floor of the current usage percentage.
  return Math.floor(100 - percentRemaining)
}

// Centralised state machine shared by UBB and legacy renderers. Mapping plan
// + quota state to a single scenario keeps the control flow in one place and
// guarantees the two copy paths handle the same set of cases.
type QuotaScenario =
  | 'free-exceeded'
  | 'free-almost-out'
  | 'free-approaching'
  | 'free-default'
  | 'individual-overage-active'
  | 'individual-premium-exceeded'
  | 'individual-premium-approaching'
  | 'individual-default'
  | 'pro-plus-overage-active'
  | 'pro-plus-premium-exceeded'
  | 'pro-plus-premium-approaching'
  | 'max-overage-active'
  | 'max-premium-exceeded'
  | 'max-premium-approaching'
  | 'business-premium-exceeded-upgradeable'
  | 'business-premium-approaching-upgradeable'
  | 'business-premium-exceeded-locked'
  | 'business-premium-approaching-locked'
  | 'enterprise-premium-exceeded-purchasable'
  | 'enterprise-premium-exceeded-locked'
  | 'enterprise-premium-approaching-purchasable'
  | 'enterprise-premium-approaching-locked'

// Footer-level thresholds for the "approaching" and (optional) "almost-out"
// tiers. UBB uses the same values as `use-quota-banner.ts` so the picker
// footer and the banner trigger at identical quota levels; legacy uses the
// historical FREE_QUOTA_TRIGGER / PREMIUM_QUOTA_TRIGGER constants so its
// behaviour is unchanged from main.
interface QuotaThresholds {
  freeApproaching: number
  freeAlmostOut?: number
  premiumApproaching: number
}

const UBB_THRESHOLDS: QuotaThresholds = {
  freeApproaching: APPROACHING_THRESHOLD,
  freeAlmostOut: ALMOST_OUT_THRESHOLD,
  premiumApproaching: APPROACHING_THRESHOLD,
}

const LEGACY_THRESHOLDS: QuotaThresholds = {
  freeApproaching: FREE_QUOTA_TRIGGER,
  premiumApproaching: PREMIUM_QUOTA_TRIGGER,
}

function detectQuotaScenario(e: EntitlementSnapshot, thresholds: QuotaThresholds): QuotaScenario | null {
  const {
    plan,
    premiumChatQuotaRemaining,
    premiumInteractionsQuotaExceeded,
    chatQuotaRemaining,
    chatQuotaExceeded,
    canPurchaseAdditionalQuota,
    canUpgradePlan,
    overagesEnabled,
  } = e

  if (plan === CopilotPlan.IndividualFree) {
    if (chatQuotaExceeded) return 'free-exceeded'
    if (thresholds.freeAlmostOut !== undefined && chatQuotaRemaining <= thresholds.freeAlmostOut) {
      return 'free-almost-out'
    }
    if (chatQuotaRemaining <= thresholds.freeApproaching) return 'free-approaching'
    return 'free-default'
  }

  // checking student to maintain parity
  if (plan === CopilotPlan.IndividualPro || plan === CopilotPlan.IndividualStudent) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'individual-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'individual-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'individual-premium-approaching'
    }
    return 'individual-default'
  }

  if (plan === CopilotPlan.IndividualProPlus) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'pro-plus-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'pro-plus-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'pro-plus-premium-approaching'
    }
    return null
  }

  if (plan === CopilotPlan.IndividualMax) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'max-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'max-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'max-premium-approaching'
    }
    return null
  }

  if (plan === CopilotPlan.Business) {
    if (overagesEnabled) return null
    if (premiumInteractionsQuotaExceeded) {
      return canUpgradePlan ? 'business-premium-exceeded-upgradeable' : 'business-premium-exceeded-locked'
    }
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching) {
      return canUpgradePlan ? 'business-premium-approaching-upgradeable' : 'business-premium-approaching-locked'
    }
    return null
  }

  if (plan === CopilotPlan.Enterprise) {
    if (overagesEnabled) return null
    if (premiumInteractionsQuotaExceeded) {
      return canPurchaseAdditionalQuota
        ? 'enterprise-premium-exceeded-purchasable'
        : 'enterprise-premium-exceeded-locked'
    }
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching) {
      return canPurchaseAdditionalQuota
        ? 'enterprise-premium-approaching-purchasable'
        : 'enterprise-premium-approaching-locked'
    }
    return null
  }

  return null
}

// For UBB scenario we don't need 'exceeded' cases as the model picker is disabled/unclickable
function renderUbbScenario(
  scenario: QuotaScenario | null,
  e: EntitlementSnapshot,
  hasSingleEnabledModel = false,
): ReactNode {
  const upgradeLinkUrl = UPGRADE_PLAN_URL_NEW
  const upgradeSuffix = hasSingleEnabledModel ? ' to access more models and higher limits.' : ' to increase your limit.'
  // Telemetry discriminator for any Upgrade click in this path. When the picker
  // has a single enabled model the CTA is shown for a different reason, so we
  // compose that into the context. The 'single-enabled-model' fallback is never
  // used (null scenario renders no link) but keeps the value well-typed.
  const upgradeContext: UpgradeLinkContext =
    scenario === null ? 'single-enabled-model' : hasSingleEnabledModel ? `${scenario}-single-enabled-model` : scenario
  const enableAdditionalUrl = MANAGE_BUDGET_URL
  const resetDate = formatQuotaResetLabel(e.quotaResetAt)

  switch (scenario) {
    case 'free-almost-out':
      return (
        <>
          You&apos;re almost out of your included usage with the free plan.
          {e.canUpgradePlan && (
            <>
              {' '}
              <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} />
              {upgradeSuffix}
            </>
          )}
        </>
      )
    case 'free-approaching':
      return (
        <>
          You&apos;re approaching the limit of your included usage with the free plan.
          {e.canUpgradePlan && (
            <>
              {' '}
              <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} />
              {upgradeSuffix}
            </>
          )}
        </>
      )
    case 'free-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-overage-active':
    case 'pro-plus-overage-active':
    case 'max-overage-active':
      return (
        <>
          <span>
            {`You're now using additional usage until your included credits reset${resetDate ? ` ${resetDate}` : ''}.`}
          </span>{' '}
          <ManageBillingLink url={MANAGE_BUDGET_URL} />
        </>
      )
    case 'individual-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-premium-approaching':
    case 'pro-plus-premium-approaching':
    case 'max-premium-approaching':
      return e.canUpgradePlan ? (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to increase your limit or{' '}
          <EnableAdditionalLink label="enable additional usage" url={enableAdditionalUrl} /> to get more.
        </>
      ) : (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <EnableAdditionalLink label="Enable additional usage" url={enableAdditionalUrl} /> to get more.
        </>
      )
    case 'business-premium-approaching-upgradeable':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <UpgradeLink url={upgradeLinkUrl} context={upgradeContext} /> to increase your limit.
        </>
      )
    case 'business-premium-approaching-locked':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <AdminBold label="Ask your admin to upgrade" /> to increase your limit.
        </>
      )
    case 'enterprise-premium-approaching-purchasable':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <EnableAdditionalLink label="Enable additional usage" url={enableAdditionalUrl} /> to get more usage after the
          limit is reached.
        </>
      )
    case 'enterprise-premium-approaching-locked':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits. Ask your
          admin to <AdminBold label="enable additional usage" /> to get more usage.
        </>
      )
    case null:
      return null
    default:
      // For UBB, we shouldn't have any 'exceeded' scenarios because the picker is disabled when over quota. If we do hit one of those scenarios, or any other unexpected scenario, log it and return null so the footer doesn't render at all.
      return null
  }
}

function renderLegacyScenario(scenario: QuotaScenario | null, e: EntitlementSnapshot): ReactNode {
  // Telemetry discriminator for any Upgrade click in this path. The 'free-exceeded'
  // fallback is never used (null scenario renders no link) but keeps it well-typed.
  const upgradeContext: UpgradeLinkContext = scenario ?? 'free-exceeded'
  switch (scenario) {
    case 'free-exceeded':
      return e.canUpgradePlan ? (
        <>
          You have used all requests available this month. <UpgradeLink context={upgradeContext} />.
        </>
      ) : (
        <>You have used all requests available this month.</>
      )
    // Legacy thresholds (LEGACY_THRESHOLDS) never produce 'free-almost-out';
    // included for exhaustive switch coverage only.
    case 'free-almost-out':
      return null
    case 'free-approaching':
      return e.canUpgradePlan ? (
        <>
          You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month.{' '}
          <UpgradeLink context={upgradeContext} /> to increase your limit.
        </>
      ) : (
        <>You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month.</>
      )
    case 'free-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-overage-active':
    case 'pro-plus-overage-active':
      return (
        <>
          You have used all premium requests available this month. You will be charged per premium request until the
          limit resets on {e.resetDate}. <ManageBillingLink />
        </>
      )
    case 'individual-premium-exceeded':
      return e.canUpgradePlan ? (
        <>
          You have used all premium requests available this month. <UpgradeLink context={upgradeContext} /> to increase
          your limit. Limit resets on {e.resetDate}.
        </>
      ) : (
        <>You have used all premium requests available this month. Limit resets on {e.resetDate}.</>
      )
    case 'individual-premium-approaching':
      return e.canUpgradePlan ? (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <UpgradeLink context={upgradeContext} /> to increase your limit.
        </>
      ) : (
        <>You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.</>
      )
    case 'individual-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink context={upgradeContext} /> to access more models and higher limits.
        </>
      ) : null
    case 'pro-plus-premium-exceeded':
      return (
        <>
          You have used all premium requests available this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more. Limit resets on {e.resetDate}.
        </>
      )
    case 'pro-plus-premium-approaching':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more.
        </>
      )
    case 'business-premium-exceeded-upgradeable':
      return (
        <>
          You have used all premium requests available this month. <UpgradeLink context={upgradeContext} /> to increase
          your limit. Limit resets on {e.resetDate}.
        </>
      )
    case 'business-premium-approaching-upgradeable':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <UpgradeLink context={upgradeContext} /> to increase your limit.
        </>
      )
    case 'business-premium-exceeded-locked':
      return (
        <>
          You have reached your monthly limit for premium requests. <AdminBold label="Ask your admin to upgrade" /> to
          increase your limit. Limit resets on {e.resetDate}.
        </>
      )
    case 'business-premium-approaching-locked':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <AdminBold label="Ask your admin to upgrade" /> to increase your limit.
        </>
      )
    case 'enterprise-premium-exceeded-purchasable':
      return (
        <>
          You have used all premium requests available this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to increase your limit. Limit resets on{' '}
          {e.resetDate}.
        </>
      )
    case 'enterprise-premium-exceeded-locked':
      return (
        <>
          You have reached your monthly limit for premium requests. Ask your admin to{' '}
          <AdminBold label="enable additional requests" /> to get more usage. Limit resets on {e.resetDate}.
        </>
      )
    case 'enterprise-premium-approaching-purchasable':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more usage after the limit is reached.
        </>
      )
    case 'enterprise-premium-approaching-locked':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month. Ask your admin to{' '}
          <AdminBold label="enable additional requests" /> to get more usage.
        </>
      )
    case null:
      return null
  }
}
